import { SwitchToggle } from './SwitchToggle';
import { GameEvent } from 'game/events/gameEvent';
import { EventName } from 'game/events/eventsName';

import { MonoBehavior } from 'game/core/monoBehavior';
import { Store } from 'game/store/gameStore';
import { GAEvent, GAEventNames } from 'game/analytics/events';
import { Container, Sprite, Text } from 'pixi.js';
import { addToUI } from 'game/core/Playable2DRootContainer';
import { getScreenSize } from 'game/core/screen';
import CloseSettings from './CloseSettings';
import { Title } from './Title';
import { DESKTOP_UI_WIDTH, Displays, SETTINGS_WIDTH } from 'constants/layout';
import { GameName } from 'constants/difficulty';
import gsap, { Power1 } from 'gsap';
import { Layout } from 'game/LayoutManager';
import { getIcon, onAssetsLoad } from 'game/preloadGameAssets';
import { translate } from 'game/translate';
import { settingLabelStyle } from './fileStyle';
import { openFeedbackPopup } from 'game/feedback';
import { addHoverEffect } from './hover';
import { KingPopup } from 'game/KingPopup';

export class GameSettings extends MonoBehavior {
    options = [
        {
            key: 'sounds',
            default: Store.soundOn,
            action: this.soundsToggle,
            visible: [GameName.KLONDIKE, GameName.SPIDER, GameName.FREECELL],
        },
        {
            key: 'autocomplete',
            default: Store.autocomplete,
            action: this.autocompleteToggle,
            visible: [GameName.KLONDIKE, GameName.SPIDER, GameName.FREECELL],
        },
        {
            key: 'leftHanded',
            default: Store.leftHanded,
            action: this.leftHandedToggle,
            visible: [GameName.KLONDIKE, GameName.SPIDER, GameName.FREECELL],
        },
        {
            key: 'cardHighlight',
            default: Store.cardHighlighting,
            action: this.cardHighlightToggle,
            visible: [GameName.SPIDER, GameName.FREECELL],
        },
        {
            key: 'autoPlay',
            default: Store.autoPlay,
            action: this.autoPlayToggle,
            visible: [GameName.KLONDIKE, GameName.FREECELL],
        },
    ];
    switches = [] as SwitchToggle[];
    container: Container;
    settingsTitle: Title;
    closeSettingsButton: CloseSettings;
    hideOptions: () => void;
    feedbackButton: Container;

    constructor(hideOptions: () => void) {
        super();
        this.hideOptions = hideOptions;
        this.feedbackButton = new Container();
        this.container = new Container();
        this.container.visible = false;
        this.closeSettingsButton = new CloseSettings(this.hideOptions);
        this.settingsTitle = new Title('settings');

        this.container.addChild(this.settingsTitle.container);
        this.container.addChild(this.closeSettingsButton.container);
        addToUI(this.container);
        this.container.position.set(getScreenSize().width + SETTINGS_WIDTH * 0.5, 100);
        this.container.zIndex = 10;

        this.options.forEach((opt, index) => {
            const toggleSwitch = new SwitchToggle(opt.key, opt.default, opt.action);
            this.container.addChild(toggleSwitch.container);
            this.switches.push(toggleSwitch);
        });

        onAssetsLoad(this.drawFeedbackButton.bind(this));
    }

    async drawFeedbackButton() {
        this.container.addChild(this.feedbackButton);
        const icon = await getIcon(`settings/feedback`, false);
        this.feedbackButton.addChild(icon);
        icon.width = 24;
        icon.height = 20;
        const txt = new Text({
            text: translate('leaveFeedback'),
            style: settingLabelStyle,
        });
        this.feedbackButton.addChild(txt);
        icon.position.set(-24, 9);
        this.feedbackButton.position.x = -txt.width / 2 + 24;
        this.feedbackButton.eventMode = 'static';
        this.feedbackButton.cursor = 'pointer';
        this.feedbackButton.on('pointerdown', () => {
            openFeedbackPopup('button');
            GameEvent.emit(EventName.CLOSE_SETTINGS);
        });
        addHoverEffect(this.feedbackButton);
    }
    handleOptions(height: number) {
        const visibleSwitches = [] as SwitchToggle[];
        this.options.forEach((option, index) => {
            if (option.visible.includes(Store.name)) visibleSwitches.push(this.switches[index]);
            else this.switches[index].container.visible = false;
        });
        visibleSwitches.forEach((switchToggle, index) => {
            switchToggle.container.position.set(-170, 120 + 67 * index); // TODO move logic to switch component or create Setting class
            switchToggle.switch.position.set(301, 5);
            switchToggle.showUnderline(index < visibleSwitches.length - 1);
            switchToggle.container.visible = true;
        });
        this.feedbackButton.position.y = height - 50;
    }

    soundsToggle() {
        GameEvent.emit(EventName.TOGGLE_SOUND);
        GAEvent(GAEventNames.GAME_SETTINGS_SOUNDS, {
            status: Store.soundOn ? 'ON' : 'OFF', //status after change
        });
    }

    autocompleteToggle() {
        GameEvent.emit(EventName.TOGGLE_AUTOCOMPLETE);
        GAEvent(GAEventNames.GAME_SETTINGS_AUTOCOMPLETE, {
            status: Store.autocomplete ? 'ON' : 'OFF', //status after change
        });
    }

    leftHandedToggle() {
        GameEvent.emit(EventName.TOGGLE_LEFT_HANDED);
        GAEvent(GAEventNames.GAME_SETTINGS_HAND, {
            hand: Store.leftHanded ? 'Left' : 'Right', //status after change
        });
    }
    cardHighlightToggle() {
        GameEvent.emit(EventName.TOGGLE_CARD_HIGHLIGHTING);
        GAEvent(GAEventNames.GAME_SETTINGS_HIGHLIGHT, {
            status: Store.cardHighlighting ? 'ON' : 'OFF', //status after change
        });
    }
    autoPlayToggle() {
        GameEvent.emit(EventName.TOGGLE_AUTOPLAY);
        GAEvent(GAEventNames.GAME_SETTINGS_AUTOPLAY, {
            status: Store.autoPlay ? 'ON' : 'OFF', //status after change
        });
    }

    hide(easeTime: number) {
        const { width, height } = getScreenSize();
        const display = Layout.getDisplay();

        let targetX = 0;
        switch (display) {
            case Displays.DESKTOP:
                targetX = width + DESKTOP_UI_WIDTH * 0.5;
                break;
            case Displays.TABLET:
                targetX = width + 0.5 * this.container.width;
                break;
            case Displays.MOBILE:
                targetX = width + 0.5 * this.container.width;
                break;
        }
        if (easeTime) {
            gsap.to(this.container.position, {
                x: width + DESKTOP_UI_WIDTH * 0.5,
                y: 0,
                ease: Power1.easeInOut,
                duration: easeTime,
            });
        } else {
            this.container.position.set(targetX, 0);
        }
    }
    show(easeTime: number) {
        this.container.visible = true;

        const { width } = getScreenSize();
        const display = Layout.getDisplay();
        let targetX = 0;
        switch (display) {
            case Displays.DESKTOP:
            case Displays.TABLET:
                targetX = width - DESKTOP_UI_WIDTH * 0.5;
                break;
            case Displays.MOBILE:
                targetX = width * 0.5;
                break;
        }

        if (easeTime) {
            gsap.to(this.container.position, {
                x: targetX,
                y: 0,
                ease: Power1.easeInOut,
                duration: easeTime,
            });
        } else {
            this.container.position.set(targetX, 0);
        }
    }
}
